import { DialogTitle, useDialogClose } from "@/src/components/ui/dialog";
import { apiCreatedDate, Option } from "./model";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { useFetch } from "@/src/lib/useFetch";
import { Input } from "@/src/components/ui/input";
import { format } from "date-fns";
import { DatePicker } from "@/src/components/ui/date-picker";
import { Button } from "@/src/components/ui/button";
import { useToast } from "@/src/components/ui/use-toast";
import { Checkbox } from "@/src/components/ui/checkbox";
import { useState } from "react";
import { Dictionary, DictionaryControl } from "@/src/components/forms/Dictionary";

const schema = z.object({
  yachtId: z.string().uuid(),
  createInApi: z.boolean(),
  periodFrom: z.string().datetime(),
  periodTo: z.string().datetime(),
});

const editSchema = z.object({
  offerId: z.string().uuid().nullable(),
});

export type OptionForm = z.infer<typeof schema>;

export function EditOption({ option }: { option?: Option }) {
  const editMode = !!option;
  const [offerId, setOfferId] = useState<string | null>(option?.offer?.offer?.id ? option.offer.offer.id : null);
  const form = useForm<OptionForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      yachtId: option?.yacht.id,
      periodFrom: option?.startDate,
      periodTo: option?.endDate,
      createInApi: true,
    },
  });
  const closeDialog = useDialogClose();

  const { toast } = useToast();

  const onEdit = form.handleSubmit(
    async () => {
      try {
        await fetch(
          `/api/options/option/${option!.id}`,
          "PATCH",
          editSchema.parse({
            offerId,
          }),
        );
        toast({
          title: "Option updated",
          variant: "default",
        });
        closeDialog();
      } catch (e: any) {
        console.error(e);
        toast({
          title: "Failed to update the option",
          description: e.message || "An error occurred while updating the option",
          variant: "destructive",
        });
      }
    },
    (e) => console.error(e, form.getValues()),
  );

  const onCreate = form.handleSubmit(
    async (value) => {
      try {
        await fetch(`/api/options/options`, "POST", value);
        toast({
          title: "Option saved",
          variant: "default",
        });
        closeDialog();
      } catch (e: any) {
        console.error(e);
        toast({
          title: "Failed to create an option",
          description: e.message || "An error occurred while saving the option",
          variant: "destructive",
        });
      }
    },
    (e) => console.error(e, form.getValues()),
  );

  const { fetch, isFetching } = useFetch();

  return (
    <>
      <DialogTitle>{!option ? "Add an option" : "View option"}</DialogTitle>
      <DictionaryControl value={"asd" as any} onChange={() => {}} onBlur={() => {}} />
      <Form {...form}>
        <form className="flex flex-col gap-2" onSubmit={editMode ? onEdit : onCreate}>
          <ScrollArea className="sm:max-h-[70vh] p-3 overflow-y-scroll overflow-x-visible">
            <div className="flex flex-col gap-2">
              <FormField<OptionForm, "yachtId">
                name="yachtId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Yacht ID</FormLabel>
                    <FormControl>
                      <Input readOnly={editMode} {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />

              {!editMode ? (
                <FormField<OptionForm, "createInApi">
                  name="createInApi"
                  render={({ field }) => (
                    <FormItem required>
                      <FormItem className="flex gap-2 items-center space-y-0 py-[6px] px-4">
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                        <FormLabel className="font-light text-base">Create in API</FormLabel>
                      </FormItem>
                    </FormItem>
                  )}
                />
              ) : apiCreatedDate(option) ? (
                <div className="py-2 font-medium">Created in the API @ {apiCreatedDate(option)}</div>
              ) : null}

              {editMode && (
                <div className="py-2 flex flex-col gap-2">
                  <FormLabel>Offer ID</FormLabel>
                  <Input
                    value={offerId ?? ""}
                    onChange={(e) => {
                      if (e.currentTarget.value === "") {
                        setOfferId(null);
                      } else {
                        setOfferId(e.currentTarget.value);
                      }
                    }}
                  />
                </div>
              )}

              <FormField<OptionForm, "periodFrom">
                name="periodFrom"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormLabel>Period from</FormLabel>
                    <FormControl>
                      <DatePicker
                        readOnly={editMode}
                        onChange={(v: any) => {
                          field.onChange(v ? format(v, "yyyy-MM-dd'T'00:00:00'Z'") : null);
                        }}
                        value={field.value ? new Date(field.value) : undefined}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField<OptionForm, "periodTo">
                name="periodTo"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1">
                    <FormLabel>Period to</FormLabel>
                    <FormControl>
                      <DatePicker
                        readOnly={editMode}
                        onChange={(v: any) => {
                          field.onChange(v ? format(v, "yyyy-MM-dd'T'00:00:00'Z'") : null);
                        }}
                        value={field.value ? new Date(field.value) : undefined}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <Button disabled={isFetching} className="mt-4">
                {editMode ? "Save option" : "Create option"}
              </Button>
            </div>
          </ScrollArea>
        </form>
      </Form>
    </>
  );
}
